<template>
  <BaseCenteredModal
    v-model:is-modal-open="localIsModalOpen"
    :title="$t('global.message_text')"
    size="large"
  >
    <template #body>
      <div class="flex w-full flex-col gap-3">
        <div class="flex flex-col gap-4 pt-1">
          <!--#region form  -->
          <div class="grid-form items-end">
            <BaseInput
              :label="$t('attributes.title')"
              :placeholder="$t('attributes.title')"
              name="title"
              :required="true"
              v-model="message_text.title"
              :errors="errors.title"
            />

            <BaseSelectSimple
              v-if="metadata.languages"
              :key="message_text.language"
              :label="$t('attributes.language')"
              :placeholder="$t('attributes.language')"
              name="language"
              :defaultValue="
                metadata.languages?.find(
                  (type) => type.id === message_text.language
                )
              "
              :options="metadata.languages"
              :errors="errors.language"
              :required="true"
              @setSelected="getInputValue"
            />

            <BaseSelectSimple
              v-if="metadata.groupes"
              :key="message_text.group"
              :label="$t('attributes.group')"
              :placeholder="$t('attributes.group')"
              name="group"
              :defaultValue="
                metadata.groupes?.find((type) => type.id === message_text.group)
              "
              :options="metadata.groupes"
              :errors="errors.group"
              :required="true"
              @setSelected="getInputValue"
            />

            <BaseSelectSimple
              v-if="metadata.types"
              :key="message_text.type"
              :label="$t('attributes.type')"
              :placeholder="$t('attributes.type')"
              :name="$t('attributes.type')"
              :options="metadata.types"
              :defaultValue="
                metadata.types?.find((type) => {
                  return type.id === message_text.type
                })
              "
              :required="true"
              :errors="errors.type"
              @setSelected="getInputValue"
            />
          </div>
          <!--#endregion -->
          <!--#region variables -->

          <div class="border p-6">
            <div class="flex justify-around">
              <BaseButton
                v-for="(variableGroup, index) in metadata.variables"
                :key="index"
                :title="
                  $t(
                    'enums.message_text.groups_variables.' + index.toLowerCase()
                  )
                "
                color="default"
                :isOutline="true"
                @click="openVariablesGroup(variableGroup, index)"
              />
            </div>
            <div v-if="isEventVariable" class="grid grid-cols-3 p-3">
              <BaseButton
                v-for="(variableGroup, index) in eventVariables"
                :key="index"
                :title="index"
                color="primary"
                :isOutline="true"
                size="small"
                scopedClasses="m-1"
                @click="openVariablesGroup(variableGroup, index)"
              />
            </div>

            <div v-if="messageVariables.length" class="p-3">
              <span
                v-for="(variable, index) in messageVariables"
                :key="index"
                @click="variableAssigned(variable)"
                class="mt-2 mr-2.5 inline-flex flex-1 items-center rounded-md border border-gray-700 px-1 py-0.5 text-sm"
              >
                @{{ variable.value }}
              </span>
            </div>
          </div>

          <!--#endregion -->
          <!--#region wysiwyg -->
          <BaseWysiwyg
            v-if="this.message_text.type"
            :displayFormatButton="message_text.type.toUpperCase() === 'EMAIL'"
            label="message"
            ref="editorForm"
            v-model="message_text.text"
            name="text"
            :suggestionVariables="allVariables"
            :required="true"
            :value="message_text.text"
            :errors="errors.text"
            @change.self="getInputValue"
          />
          <!--#endregion -->
        </div>
      </div>
    </template>
    <template #footer>
      <BaseButton
        color="primary"
        icon="paper-plane"
        :title="buttonText"
        @click="submitMessageText"
      />
      <BaseButton :title="$t('button.close')" color="dark" @click="close" />
    </template>
  </BaseCenteredModal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseCenteredModal from '@c/addf-package/components/modal/BaseCenteredModal.vue'

export default {
  name: 'MessageTextFormDialog',
  components: { BaseCenteredModal },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    message_text_load_id: {
      required: false
    }
  },
  data() {
    return {
      editor: null,
      messageVariables: [],
      eventVariables: [],
      isEventVariable: false
    }
  },
  computed: {
    ...mapGetters({
      message_text: 'messageText/getMessageText',
      metadata: 'messageText/getMessageTextMetadata',
      errors: 'auth/getErrors'
    }),
    localIsModalOpen: {
      get() {
        return this.isOpen
      },
      set(value) {
        if (!value) this.close()
      }
    },
    buttonText() {
      if (isNaN(this.message_text_load_id)) {
        return this.$t('button.add', { var: '' })
      }
      return this.$t('button.edit', { var: '' })
    },
    allVariables() {
      let allVariables = []
      for (let i in this.metadata.allVariables) {
        allVariables.push(this.metadata.allVariables[i])
      }
      return allVariables
    }
  },
  methods: {
    ...mapActions({
      fetchNewMessageText: 'messageText/fetchNewMessageText',
      fetchNewMessageTextById: 'messageText/fetchMessageTextById',
      addMessageText: 'messageText/storeMessageText',
      editMessageText: 'messageText/updateMessageText'
    }),
    getInputValue({ attribute, value }) {
      this.message_text[attribute] = value
    },
    submitMessageText() {
      if (!isNaN(this.message_text_load_id)) {
        this.editMessageText({
          messageText: this.message_text
        }).then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.updated_m', {
              var: this.$t('global.message_text')
            })
          )
          this.close()
        })
      } else {
        this.addMessageText({
          messageText: this.message_text
        }).then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.created_m', {
              var: this.$t('global.message_text')
            })
          )
          this.close()
        })
      }
    },
    close() {
      this.$emit('close')
    },
    variableAssigned(variable) {
      this.$refs.editorForm.setTextAtPosition(variable)
    },
    openVariablesGroup(group, index) {
      if (index !== 'Event') {
        this.isEventVariable = false
        this.messageVariables = group
      } else {
        this.isEventVariable = true
        this.eventVariables = group
        this.messageVariables = []
      }
    }
  },
  watch: {
    async isOpen(newValue, oldValue) {
      if (newValue && newValue !== oldValue) {
        if (!isNaN(this.message_text_load_id)) {
          await this.fetchNewMessageTextById({ id: this.message_text_load_id })
        } else {
          await this.fetchNewMessageText()
        }
      }
    }
  }
}
</script>
